import { Language } from '@codemirror/language';
import { Extension } from '@codemirror/state';
import { forwardRef, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { PLACEHOLDER_MESSAGES } from './codeEditorMessages.js';
import type { InlineDiagnostic } from './inline-diagnostic.js';
import { codeEditorLanguages } from './languages/languages.js';
import { ErrorFallback as EditorErrorFallback } from '../../core/components/error-fallback/ErrorFallback.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { createStratoLogger } from '../../core/sdk/sdk-logger.js';
import type { AutocompleteProvider } from '../base-code-editor/auto-completion/autocompletion.extension.js';
import type { ListAutocompletionConfig } from '../base-code-editor/auto-completion/list/list-autocompletion-types.js';
import {
  BaseCodeEditor,
  type SharedBaseCodeEditorProps,
} from '../base-code-editor/BaseCodeEditor.js';
import type { EditorLanguageConfiguration } from '../base-code-editor/configuration/configuration.js';
import type { GutterConfiguration } from '../base-code-editor/configuration/getDefaultConfigurations.js';

const COMPONENT_NAME = 'CodeEditor';

/**
 * Props for the `CodeEditor` component.
 * @public
 */
export interface CodeEditorBaseProps extends SharedBaseCodeEditorProps {
  /**
   * The language for syntax highlighting and autocompletion.
   * @defaultValue 'other'
   */
  language?: 'json' | 'js' | 'jsx' | 'ts' | 'tsx' | 'md' | 'yaml' | 'other';
  // DEPRECATION: [APPDEV-19108] BREAKING-CHANGE: Remove `gutterConfiguration`.
  /**
   * Provides the gutter configuration to display a marker next to the fold gutter.
   * @deprecated Use the `diagnostics` and `showLintGutter` props for per-line validation markers instead.
   * Will be removed in a future major release.
   */
  gutterConfiguration?: GutterConfiguration;
  /**
   * Diagnostics to render inside the editor (underlines, hover tooltips).
   * When provided, the built-in JSON linter is overridden.
   */
  diagnostics?: InlineDiagnostic[];
  /**
   * When `true`, shows a severity marker in a gutter column next to each diagnostic line.
   * Has no effect when `diagnostics` is `undefined` or empty. Takes precedence over
   * `gutterConfiguration` when both are set.
   * @defaultValue false
   */
  showLintGutter?: boolean;
}

/**
 * Props for the `CodeEditor` in controlled usage.
 * @public
 */
export type CodeEditorControlledProps = {
  /**
   * The value of the editor in controlled mode.
   * @defaultValue ''
   */
  value?: string;
  /** Not allowed in controlled mode. */
  defaultValue?: never;
};

/**
 * Props for the `CodeEditor` in uncontrolled usage.
 * @public
 */
export type CodeEditorUncontrolledProps = {
  /** Not allowed in uncontrolled mode. */
  value?: never;
  /**
   * The default value of the editor for uncontrolled usage.
   */
  defaultValue?: string;
  /**
   * `isControlled` has no effect without `value`.
   * @deprecated [APPDEV-17404] BREAKING-CHANGE: Will be removed when controlled behaviour becomes the default.
   */
  isControlled?: never;
};

/**
 * Props for the `CodeEditor` component.
 * @public
 */
export type CodeEditorProps = CodeEditorBaseProps &
  (CodeEditorControlledProps | CodeEditorUncontrolledProps);

/**
 * Ref handle exposed by the `CodeEditor` component.
 * @public
 */
export type CodeEditorRef = {
  /**
   * Moves focus to the editor.
   */
  focus: () => void;
  /**
   * Selects the text within the given character-offset range.
   */
  selectRange: (range: { from: number; to: number }) => void;
  /**
   * The underlying editor container DOM element.
   */
  readonly element: HTMLDivElement | null;
  /**
   * Moves the cursor to the given character offset, or to the end of the document when `'end'` is passed.
   */
  setCursorPosition: (position: number | 'end') => void;
};

const CodeEditorInner = /* @__PURE__ */ forwardRef<
  CodeEditorRef,
  CodeEditorProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const {
    id,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
    controlState,
    defaultFolding,
    folding,
    onFoldingChange,
    spellCheck = false,
    language = 'other',
    placeholder = intl.formatMessage(PLACEHOLDER_MESSAGES[language]),
    readOnly = false,
    lineWrap = false,
    fullHeight = false,
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    // DEPRECATION: [APPDEV-19108] BREAKING-CHANGE: Remove gutterConfiguration prop.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    gutterConfiguration,
    diagnostics,
    showLintGutter = false,
    size = 'default',
    required = false,
    // DEPRECATION: [APPDEV-17404] BREAKING-CHANGE: Remove isControlled prop.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    isControlled = false,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const hasDiagnostics = diagnostics !== undefined;
  const config = useMemo((): EditorLanguageConfiguration => {
    return getLanguageConfig(
      codeEditorLanguages[language],
      readOnly,
      hasDiagnostics,
    );
  }, [language, readOnly, hasDiagnostics]);

  const valueProps = (
    value !== undefined ? { value } : { defaultValue }
  ) satisfies Partial<{ value: string; defaultValue: string }>;

  return (
    <BaseCodeEditor
      id={id}
      ref={forwardedRef}
      placeholder={placeholder}
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
      controlState={controlState}
      readOnly={readOnly}
      folding={folding}
      defaultFolding={defaultFolding}
      onFoldingChange={onFoldingChange}
      lineWrap={lineWrap}
      {...valueProps}
      onChange={onChange}
      spellCheck={spellCheck}
      fullHeight={fullHeight}
      configuration={config}
      onBlur={onBlur}
      onFocus={onFocus}
      // DEPRECATION: [APPDEV-19108] BREAKING-CHANGE: Remove gutterConfiguration prop.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      gutterConfiguration={gutterConfiguration}
      diagnostics={diagnostics}
      showLintGutter={showLintGutter}
      size={size}
      required={required}
      // DEPRECATION: [APPDEV-17404] BREAKING-CHANGE: Remove isControlled prop.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      isControlled={isControlled}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    />
  );
});

/**
 * The `CodeEditor` provides a text input field that is specifically designed for editing code.
 * It further offers properties to configure e.g. syntax highlighting, spell checks or line wrapping.
 * Once the editor is focused via the keyboard, the user must press `Enter` to start editing and `Escape` to quit editing and return to the keyboard navigation flow.
 * @public
 */
export const CodeEditor = /* @__PURE__ */ forwardRef<
  CodeEditorRef,
  CodeEditorProps
>((props, ref) => {
  const stratoLogger = useMemo(
    () => createStratoLogger.bind({ id: props.id })(COMPONENT_NAME),
    [props.id],
  );

  return (
    <ErrorBoundary
      onError={(error) => stratoLogger.error(error?.message, error)}
      FallbackComponent={(fallbackProps) => (
        <EditorErrorFallback {...fallbackProps} />
      )}
    >
      <CodeEditorInner {...props} ref={ref} />
    </ErrorBoundary>
  );
});

/**
 * If readOnly is true then there is no autocompletion.
 * When custom diagnostics are supplied, built-in language linter extensions
 * (e.g. jsonParseLinter) are stripped from `extendWith` so they don't conflict
 * with the consumer-supplied diagnostics.
 * @param languageConfig - standard language config
 * @param readOnly - whether the editor is readOnly
 * @param hasDiagnostics - whether the consumer supplies custom diagnostics
 * @returns new config with appropriate values
 * @internal
 */
export function getLanguageConfig(
  languageConfig: EditorLanguageConfiguration,
  readOnly: boolean,
  hasDiagnostics: boolean = false,
): {
  language: Language | undefined;
  autocompleteOptions:
    | {
        activateOnTyping: boolean;
        providers: (AutocompleteProvider | ListAutocompletionConfig)[];
      }
    | undefined;
  extendWith: readonly Extension[] | undefined;
} {
  return {
    language: languageConfig.language,
    autocompleteOptions: readOnly
      ? undefined
      : {
          activateOnTyping: true,
          providers: languageConfig.autocompleteOptions?.providers ?? [],
        },
    // When the consumer provides custom diagnostics, strip built-in linter
    // extensions so they don't conflict with external diagnostics.
    extendWith: hasDiagnostics ? [] : languageConfig.extendWith,
  };
}

(
  CodeEditorInner as typeof CodeEditorInner & { displayName: string }
).displayName = COMPONENT_NAME;
(CodeEditor as typeof CodeEditor & { displayName: string }).displayName =
  COMPONENT_NAME;
